import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api, formatCurrency, formatDate } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Plus, FileText, Trash2, Landmark, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import ConfirmDelete from "@/components/ConfirmDelete";
import { deleteWithUndo } from "@/lib/deleteWithUndo";
import CustomerSearch from "@/components/CustomerSearch";
import ProductSearch from "@/components/ProductSearch";
import Pagination from "@/components/Pagination";

const empty = {
  customer_id: "", product_id: "", quantity: 1, unit_price: 0, discount: 0,
  payment_status: "paid", payment_method: "cash", amount_paid: 0, notes: "",
  serial_number: "", chassis_number: "", engine_number: "",
  subsidy_case_id: "",
};

const badge = (status) => {
  const map = { paid: "bg-[#39FF14]/10 text-[#39FF14]", partial: "bg-yellow-500/15 text-yellow-300", pending: "bg-red-500/15 text-red-300" };
  return map[status] || "bg-[#121815] text-[#F8F9FA]";
};

export default function Sales() {
  const [rows, setRows] = useState([]);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(25);
  const [pageInfo, setPageInfo] = useState({ page: 1, total: 0, total_pages: 0, page_size: 25 });
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [readySubsidies, setReadySubsidies] = useState([]);
  const [subsidyChoice, setSubsidyChoice] = useState(null); // 'ask' | 'use' | 'skip'
  const [chosenSubsidyId, setChosenSubsidyId] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set("page", String(page));
      params.set("page_size", String(pageSize));
      const { data } = await api.get(`/sales?${params.toString()}`);
      const items = Array.isArray(data) ? data : (data?.items || []);
      setRows(items);
      setPageInfo({
        page: data?.page ?? 1,
        page_size: data?.page_size ?? pageSize,
        total: data?.total ?? items.length,
        total_pages: data?.total_pages ?? 1,
      });
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [page]);

  // Deep-link from Subsidy Desk → open New Sale with subsidy pre-selected.
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    const cid = searchParams.get("customer");
    const sid = searchParams.get("subsidy");
    if (cid && sid) {
      (async () => {
        try {
          const { data } = await api.get(`/customers/${cid}/ready-subsidies`);
          setReadySubsidies(data || []);
          setForm({ ...empty, customer_id: cid, subsidy_case_id: sid });
          setChosenSubsidyId(sid);
          setSubsidyChoice("use");
          setOpen(true);
          searchParams.delete("customer"); searchParams.delete("subsidy");
          setSearchParams(searchParams, { replace: true });
        } catch { /* ignore */ }
      })();
    }
    /* eslint-disable-next-line */
  }, []);

  const total = (form.unit_price * form.quantity) - form.discount;

  const submit = async (e) => {
    e.preventDefault();
    if (!form.customer_id || !form.product_id) return toast.error("Select customer and product");
    try {
      const payload = {
        ...form,
        quantity: parseInt(form.quantity) || 1,
        unit_price: parseFloat(form.unit_price) || 0,
        discount: parseFloat(form.discount) || 0,
        amount_paid: parseFloat(form.amount_paid) || 0,
      };
      await api.post("/sales", payload);
      toast.success(payload.subsidy_case_id ? "Sale recorded — subsidy case marked completed" : "Sale recorded");
      setOpen(false); setForm(empty); setReadySubsidies([]); setSubsidyChoice(null); setChosenSubsidyId(""); load();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to record sale"); }
  };

  const onCustomerSelected = async (cid) => {
    setForm({ ...form, customer_id: cid, subsidy_case_id: "" });
    setChosenSubsidyId("");
    setReadySubsidies([]);
    setSubsidyChoice(null);
    if (!cid) return;
    try {
      const { data } = await api.get(`/customers/${cid}/ready-subsidies`);
      if (data && data.length > 0) {
        setReadySubsidies(data);
        setSubsidyChoice("ask");
      }
    } catch { /* ignore — sales rep may lack scope */ }
  };

  const pickSubsidy = (subId) => {
    setChosenSubsidyId(subId);
    setForm((f) => ({ ...f, subsidy_case_id: subId }));
    setSubsidyChoice("use");
  };
  const skipSubsidy = () => { setSubsidyChoice("skip"); setForm((f) => ({ ...f, subsidy_case_id: "" })); setChosenSubsidyId(""); };

  const del = (s) =>
    deleteWithUndo({
      deletePath: `/sales/${s.id}`,
      restorePath: `/sales/${s.id}/restore`,
      label: s.invoice_no,
      entityLabel: "sale",
      onChange: load,
    });

  const selectProduct = (p) => {
    setSelectedProduct(p);
    setForm({ ...form, product_id: p?.id || "", unit_price: p?.price ?? p?.unit_price ?? 0 });
  };

  return (
    <div className="p-8 lg:p-10 max-w-[1600px]">
      <PageHeader
        title="Sales"
        subtitle="Machinery sold to your customers with invoice records."
        exportEntity="sales"
        testid="page-sales-header"
        action={
          <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setForm(empty); setSelectedProduct(null); } }}>
            <DialogTrigger asChild>
              <Button data-testid="add-sale-btn" className="bg-[#39FF14] hover:bg-[#2ECC71]">
                <Plus size={16} className="mr-2" /> New Sale
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl">
              <DialogHeader><DialogTitle className="font-heading">Record New Sale</DialogTitle></DialogHeader>
              <form onSubmit={submit} className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <Label>Customer *</Label>
                  <CustomerSearch
                    testid="sale-customer-search"
                    value={form.customer_id}
                    onChange={(c) => onCustomerSelected(c?.id || "")}
                  />
                  {form.subsidy_case_id && (() => {
                    const s = readySubsidies.find((x) => x.id === form.subsidy_case_id);
                    if (!s) return null;
                    return (
                      <div className="mt-2 bg-green-50 border border-[#39FF14]/25 rounded p-2.5 flex items-start gap-2 text-xs" data-testid="sale-linked-subsidy">
                        <Landmark size={14} className="text-[#39FF14] mt-0.5 shrink-0" />
                        <div className="flex-1">
                          <div className="font-heading font-semibold text-green-900">Linked to subsidy {s.case_no}</div>
                          <div className="text-[#39FF14]/80">{s.scheme} · Permit {s.permit_number} · Approved {formatCurrency(s.approved_amount || 0)}{s.approved_percentage ? ` (${s.approved_percentage}%)` : ""}</div>
                        </div>
                        <button type="button" className="text-[11px] text-red-600 hover:underline" onClick={skipSubsidy}>Unlink</button>
                      </div>
                    );
                  })()}
                </div>
                <div className="col-span-2">
                  <Label>Product*</Label>
                  <ProductSearch
                    testid="sale-product-search"
                    value={form.product_id}
                    product={selectedProduct}
                    onChange={(p) => selectProduct(p)}
                  />
                </div>
                <div><Label>Quantity</Label><Input type="number" min="1" value={form.quantity} onChange={(e) => setForm({ ...form, quantity: e.target.value })} /></div>
                <div><Label>Unit Price</Label><Input type="number" value={form.unit_price} onChange={(e) => setForm({ ...form, unit_price: e.target.value })} /></div>
                <div><Label>Discount</Label><Input type="number" value={form.discount} onChange={(e) => setForm({ ...form, discount: e.target.value })} /></div>
                <div><Label>Amount Paid</Label><Input type="number" data-testid="sale-amount-paid-input" value={form.amount_paid} onChange={(e) => setForm({ ...form, amount_paid: e.target.value })} /></div>
                <div>
                  <Label>Payment Status</Label>
                  <Select value={form.payment_status} onValueChange={(v) => setForm({ ...form, payment_status: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="paid">Paid</SelectItem>
                      <SelectItem value="partial">Partial</SelectItem>
                      <SelectItem value="pending">Pending</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Payment Method</Label>
                  <Select value={form.payment_method} onValueChange={(v) => setForm({ ...form, payment_method: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="cash">Cash</SelectItem>
                      <SelectItem value="bank_transfer">Bank Transfer</SelectItem>
                      <SelectItem value="cheque">Cheque</SelectItem>
                      <SelectItem value="upi">UPI</SelectItem>
                      <SelectItem value="finance">Finance / EMI</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="col-span-2"><Label>Notes</Label><Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
                <div className="col-span-2 border-t border-[#3A4640] pt-3">
                  <div className="text-xs uppercase tracking-wider text-[#8A8F98] font-heading mb-2">Machine identifiers (recommended)</div>
                  <div className="grid grid-cols-3 gap-3">
                    <div><Label className="text-xs">Serial Number</Label><Input value={form.serial_number} onChange={(e) => setForm({ ...form, serial_number: e.target.value })} data-testid="sale-serial-input" placeholder="Manufacturer serial" /></div>
                    <div><Label className="text-xs">Chassis Number</Label><Input value={form.chassis_number} onChange={(e) => setForm({ ...form, chassis_number: e.target.value })} data-testid="sale-chassis-input" placeholder="Chassis / VIN" /></div>
                    <div><Label className="text-xs">Engine Number</Label><Input value={form.engine_number} onChange={(e) => setForm({ ...form, engine_number: e.target.value })} data-testid="sale-engine-input" placeholder="Engine no." /></div>
                  </div>
                  <div className="text-[11px] text-[#8A8F98] mt-1.5">These are stamped onto every implement and become the permanent identity for warranty, service &amp; AMC.</div>
                </div>
                <div className="col-span-2 bg-[#0B0F0C] border border-[#3A4640] rounded-md p-4 flex justify-between items-center">
                  <span className="font-heading font-semibold text-[#F8F9FA]">Total Amount</span>
                  <span className="font-heading text-2xl font-extrabold text-[#39FF14]" data-testid="sale-total-preview">{formatCurrency(total)}</span>
                </div>
                <DialogFooter className="col-span-2">
                  <Button type="submit" data-testid="sale-submit-btn" className="bg-[#39FF14] hover:bg-[#2ECC71]">Record Sale</Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        }
      />

      {/* Active Subsidy Permit Found modal */}
      <Dialog open={subsidyChoice === "ask"} onOpenChange={(v) => !v && setSubsidyChoice("skip")}>
        <DialogContent className="max-w-lg" data-testid="active-subsidy-modal">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2"><Landmark size={20} className="text-[#39FF14]" /> Active Subsidy Permit Found</DialogTitle>
            <DialogDescription>
              This customer has {readySubsidies.length} subsidy {readySubsidies.length === 1 ? "case" : "cases"} ready for sale. Choose how to continue.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 max-h-[320px] overflow-y-auto" data-testid="active-subsidy-list">
            {readySubsidies.map((s) => {
              const daysToExpiry = s.permit_expiry ? Math.round((new Date(s.permit_expiry) - new Date()) / 86400000) : null;
              const warn = daysToExpiry !== null && daysToExpiry <= 15;
              return (
                <button
                  key={s.id}
                  type="button"
                  onClick={() => pickSubsidy(s.id)}
                  data-testid={`use-subsidy-${s.id}`}
                  className="w-full text-left border border-[#3A4640] hover:border-[#39FF14]/40 hover:bg-green-50/40 rounded-md p-3 transition-all"
                >
                  <div className="flex justify-between items-start">
                    <div>
                      <div className="font-mono text-xs text-[#8A8F98]">{s.case_no}</div>
                      <div className="font-heading font-semibold text-[#F8F9FA]">{s.scheme || "Scheme TBD"}</div>
                      <div className="text-xs text-[#8A8F98] mt-0.5">{s.department} · FY {s.financial_year} · {s.approved_machine_category || s.machine_category}</div>
                    </div>
                    <div className="text-right text-xs">
                      <div className="font-mono">Permit {s.permit_number || "-"}</div>
                      {s.permit_expiry && (
                        <div className={warn ? "text-red-600 font-heading font-semibold" : "text-[#8A8F98]"}>
                          {warn && <AlertTriangle size={11} className="inline mr-1" />}
                          {daysToExpiry < 0 ? "Expired" : `Expires in ${daysToExpiry} day${daysToExpiry === 1 ? "" : "s"}`}
                        </div>
                      )}
                      <div className="font-mono font-bold text-[#39FF14] mt-0.5">{formatCurrency(s.approved_amount || 0)}</div>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={skipSubsidy} data-testid="skip-subsidy-btn">Continue normal sale</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <div className="bg-[#121815] border border-[#3A4640] rounded-md shadow-sm overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[#0B0F0C] border-b border-[#3A4640] text-xs uppercase tracking-wider text-[#8A8F98] font-heading">
            <tr>
              <th className="text-left px-4 py-3">Invoice</th>
              <th className="text-left px-4 py-3">Date</th>
              <th className="text-left px-4 py-3">Customer</th>
              <th className="text-left px-4 py-3">Product</th>
              <th className="text-right px-4 py-3">Amount</th>
              <th className="text-right px-4 py-3">Balance</th>
              <th className="text-left px-4 py-3">Status</th>
              <th className="text-right px-4 py-3">Actions</th>
            </tr>
          </thead>
          <tbody data-testid="sales-table-body">
            {rows.length === 0 && <tr><td colSpan={8} className="text-center py-12 text-[#8A8F98]">No sales yet.</td></tr>}
            {rows.map((s) => (
              <tr key={s.id} className="border-b border-[#3A4640] last:border-0 hover:bg-[#0B0F0C]">
                <td className="px-4 py-3 font-mono text-xs">{s.invoice_no}</td>
                <td className="px-4 py-3 text-[#8A8F98] text-xs">{formatDate(s.sale_date)}</td>
                <td className="px-4 py-3 font-medium">{s.customer?.name || "-"}</td>
                <td className="px-4 py-3 text-[#8A8F98]">{s.product?.brand} {s.product?.model}</td>
                <td className="px-4 py-3 text-right font-mono font-semibold">{formatCurrency(s.total_amount)}</td>
                <td className="px-4 py-3 text-right font-mono">{formatCurrency(s.balance_due)}</td>
                <td className="px-4 py-3"><span className={`text-xs font-medium px-2 py-1 rounded ${badge(s.payment_status)}`}>{s.payment_status}</span></td>
                <td className="px-4 py-3 text-right">
                  <Link to={`/sales/${s.id}/invoice`} className="inline-block p-1.5 hover:bg-[#121815] rounded" data-testid={`view-invoice-${s.id}`}><FileText size={14} /></Link>
                  <ConfirmDelete
                    itemLabel={s.invoice_no}
                    entityLabel="sale"
                    onConfirm={() => del(s)}
                    testid={`sale-${s.id}`}
                  >
                    <button className="p-1.5 hover:bg-red-500/10 text-red-600 rounded ml-1" data-testid={`delete-sale-${s.id}`}><Trash2 size={14} /></button>
                  </ConfirmDelete>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <Pagination
          page={pageInfo.page}
          totalPages={pageInfo.total_pages}
          total={pageInfo.total}
          pageSize={pageInfo.page_size}
          onPageChange={setPage}
          loading={loading}
          testid="sales-pagination"
        />
      </div>
    </div>
  );
}
